 
function isNullish(value) {
    return value === null || value === undefined || value === '';
}

// Durée de validité (24h) — cookies Max-Age en secondes.
const GIFT_TTL_SEC = 24 * 60 * 60;
const GIFT_TTL_MS = GIFT_TTL_SEC * 1000;

const ARMED_COOKIE = 'mb_gift_armed';
const DISMISSED_COOKIE = 'mb_gift_dismissed';
const ADDED_COOKIE = 'mb_gift_added';

// Anciennes clés localStorage (migration puis suppression).
const LEGACY_KEYS = {
    [ARMED_COOKIE]: 'giftDispatcherArmed',
    [DISMISSED_COOKIE]: 'giftDispatcherDismissed',
    [ADDED_COOKIE]: 'giftDispatcherAdded',
};

function setGiftCookie(name, value) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
        encodeURIComponent(name) + '=' + encodeURIComponent(value) +
        '; Max-Age=' + GIFT_TTL_SEC + '; Path=/; SameSite=Lax' + secure;
}

function getGiftCookie(name) {
    const escaped = encodeURIComponent(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
}

function deleteGiftCookie(name) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
        encodeURIComponent(name) + '=; Max-Age=0; Path=/; SameSite=Lax' + secure;
}

function readGiftStorage(cookieName) {
    try {
        const raw = getGiftCookie(cookieName);
        if (raw) return JSON.parse(raw);

        const legacyKey = LEGACY_KEYS[cookieName];
        if (legacyKey && typeof localStorage !== 'undefined') {
            const legacy = localStorage.getItem(legacyKey);
            if (legacy) {
                localStorage.removeItem(legacyKey);
                return JSON.parse(legacy);
            }
        }
    } catch (error) {}
    return {};
}

function writeGiftStorage(cookieName, data) {
    try {
        const keys = Object.keys(data);
        if (keys.length === 0) {
            deleteGiftCookie(cookieName);
            return;
        }
        setGiftCookie(cookieName, JSON.stringify(data));
    } catch (error) {}
}
 
class GiftDispatcher extends HTMLElement {
    constructor() {
        super();
        this.gifts = this.querySelectorAll('input[type="hidden"]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // Le panier peut être absent selon le template : on ne déréférence pas un null.
        this.cartItems = this.cart
            ? (this.cart.querySelector('cart-items') || this.cart.querySelector('cart-drawer-items'))
            : null;
 
        this.cartURLs = {
            add: `${routes.cart_add_url}.js`,
            change: `${routes.cart_change_url}.js`,
            update: `${routes.cart_update_url}.js`,
            cart: `${routes.cart_url}.js`,
        };

        // delete permission to prevent from double cart refresh
        this.deletePermission = true;
        // Verrou d'ajout par variante : empêche un double ajout si deux événements
        // cartUpdate arrivent avant que le premier ajout ne soit terminé.
        this.addInProgress = new Set();

        // Cadeaux "armés" via le lien ?add-gift= (cookie 24h).
        this.armedVariants = this.loadTtlMap(ARMED_COOKIE);

        // Cadeaux refusés : retrait manuel après ajout auto (cookie 24h).
        this.dismissedVariants = this.loadTtlMap(DISMISSED_COOKIE);

        // Cadeaux ajoutés par nous (cookie 24h, détecte le retrait manuel).
        this.addedVariants = this.loadTtlMap(ADDED_COOKIE);

        // Codes de remise appliqués au panier lors du dernier passage (Set de codes en
        // MAJUSCULES). null = pas encore initialisé. Sert à détecter qu'un code vient d'être
        // (ré)appliqué manuellement pendant la session -> on lève alors le refus du cadeau.
        this.appliedCodes = null;

        // Option section : un seul produit offert à la fois (un nouveau éligible remplace
        // le précédent). Désactivée par défaut -> comportement multi-cadeaux inchangé.
        this.singleGift = this.dataset.singleGift === 'true';

        // Anti-boucle / anti-429 : debounce + mutex + backoff Shopify.
        this.validateTimer = null;
        this.validateInProgress = false;
        this.validateQueuedCart = null;
        this.rateLimitUntil = 0;
        this.sourceTag = 'gift-dispatcher';
    }

    markAdded(variantId) {
        this.addedVariants.set(variantId, Date.now() + GIFT_TTL_MS);
        this.saveTtlMap(ADDED_COOKIE, this.addedVariants);
    }

    unmarkAdded(variantId) {
        if(this.addedVariants.delete(variantId)) {
            this.saveTtlMap(ADDED_COOKIE, this.addedVariants);
        }
    }

    // Cookie "refus" posé quand le client retire manuellement un cadeau qu'on avait ajouté.
    setDismissedCookie(variantId) {
        this.dismissedVariants.set(variantId, Date.now() + GIFT_TTL_MS);
        this.saveTtlMap(DISMISSED_COOKIE, this.dismissedVariants);
    }

    // Cookie "refus" effacé (resaisie code promo ou re-clic ?add-gift=).
    clearDismissedCookie(variantId) {
        if(this.dismissedVariants.delete(variantId)) {
            this.saveTtlMap(DISMISSED_COOKIE, this.dismissedVariants);
        }
    }

    connectedCallback() {
        this.handleAddGiftParam();
        this.listenEvents();
        // Valider aussi au chargement : le panier peut déjà contenir un cadeau
        // ajouté lors d'une visite précédente dont les conditions ne sont plus remplies.
        this.getCart().then(cart => { if(cart) this.scheduleValidate(cart); });
    }

    // Regroupe les cartUpdate (évite N× GET /cart.js → erreur 429 Shopify).
    scheduleValidate(cartData) {
        if (cartData && cartData.items) {
            this.validateQueuedCart = cartData;
        }
        if (this.validateTimer) clearTimeout(this.validateTimer);
        this.validateTimer = setTimeout(() => this.runValidate(), 400);
    }

    async runValidate() {
        if (this.validateInProgress) {
            this.validateTimer = setTimeout(() => this.runValidate(), 400);
            return;
        }
        if (Date.now() < this.rateLimitUntil) return;

        this.validateInProgress = true;
        let cart = this.validateQueuedCart;
        this.validateQueuedCart = null;

        try {
            if (!cart || !cart.items) {
                cart = await this.getCart();
            }
            if (cart) this.validateConditions(cart);
        } finally {
            this.validateInProgress = false;
        }
    }

    // Charge une Map<variantId, expiresAt> depuis un cookie (24h).
    loadTtlMap(cookieName) {
        const map = new Map();
        try {
            const data = readGiftStorage(cookieName);
            const now = Date.now();
            let changed = false;
            for (const [id, expiresAt] of Object.entries(data)) {
                if (typeof expiresAt === 'number' && expiresAt > now) {
                    map.set(Number(id), expiresAt);
                } else {
                    changed = true;
                }
            }
            if (changed) this.saveTtlMap(cookieName, map);
        } catch (error) {}
        return map;
    }

    saveTtlMap(cookieName, map) {
        try {
            const data = {};
            for (const [id, expiresAt] of map) {
                data[id] = expiresAt;
            }
            writeGiftStorage(cookieName, data);
        } catch (error) {}
    }

    isInTtlMap(map, cookieName, variantId) {
        const expiresAt = map.get(variantId);
        if (expiresAt === undefined) return false;

        if (expiresAt <= Date.now()) {
            map.delete(variantId);
            this.saveTtlMap(cookieName, map);
            return false;
        }
        return true;
    }

    // Lien type ?add-gift=beard : on "arme" le cadeau (intention mémorisée pour la session).
    // Le cadeau n'est PAS ajouté tout de suite : il le sera dès que le panier atteint le
    // montant minimum du bloc (le code promo n'est alors plus requis).
    async handleAddGiftParam() {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('add-gift');

        if(isNullish(code)) return;

        const normalized = code.trim().toUpperCase();
        const gift = [...this.gifts].find((g) =>
            g.dataset.discountCode && g.dataset.discountCode.trim().toUpperCase() === normalized
        );

        if(!gift || isNullish(gift.dataset.variantId)) return;

        const variantId = parseInt(gift.dataset.variantId);
        // (Re)cliquer sur le lien relance une fenêtre de 24h.
        this.armedVariants.set(variantId, Date.now() + GIFT_TTL_MS);
        this.saveTtlMap(ARMED_COOKIE, this.armedVariants);

        this.clearDismissedCookie(variantId);
        this.unmarkAdded(variantId);

        // On laisse validateConditions décider selon le montant du panier.
        const cart = await this.getCart();
        if(cart) this.scheduleValidate(cart);
    }
 
    disconnectedCallback() {
        this.removeEvents();
    }
 
    listenEvents() {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            // Ignorer nos propres mises à jour panier (évite boucle add → cartUpdate → add).
            if (event && event.source === this.sourceTag) return;
            this.scheduleValidate(event && event.cartData);
        });

        // Saisie manuelle du code promo dans le panier (deployer-cart-discount).
        // Relance toutes les vérifications même si le code était déjà appliqué.
        this.onManualDiscountApply = this.handleManualDiscountApply.bind(this);
        document.addEventListener('gift:discount-manually-applied', this.onManualDiscountApply);
    }

    removeEvents() {
        if (this.onManualDiscountApply) {
            document.removeEventListener('gift:discount-manually-applied', this.onManualDiscountApply);
        }
    }
 
    async getCart() {
        if (Date.now() < this.rateLimitUntil) return null;

        return fetch(this.cartURLs.cart, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        .then(response => {
            if (response.status === 429) {
                this.rateLimitUntil = Date.now() + 15000;
                console.warn('[gift-dispatcher] Rate limit Shopify (429) — pause 15s');
                return null;
            }
            if (!response.ok) return null;
            return response.json();
        })
        .catch((error) => {
          console.error('[gift-dispatcher] getCart:', error);
          return null;
        });
    }
 
    // Lève le refus (dismissed + added) pour tous les cadeaux liés à un code promo.
    // Appelé quand le client (ré)applique volontairement un code dans le champ panier.
    resetGiftDismissalForCode(code) {
        if (isNullish(code)) return;

        const normalized = code.trim().toUpperCase();

        for (const gift of this.gifts) {
            if ((gift.dataset.discountCode || '').trim().toUpperCase() !== normalized) continue;

            const variantId = parseInt(gift.dataset.variantId);
            this.clearDismissedCookie(variantId);
            this.unmarkAdded(variantId);
        }
    }

    handleManualDiscountApply(event) {
        const normalized = (event?.detail?.code || '').trim().toUpperCase();
        if (!normalized) return;

        let matched = false;

        for (const gift of this.gifts) {
            if ((gift.dataset.discountCode || '').trim().toUpperCase() !== normalized) continue;
            if (isNullish(gift.dataset.variantId)) continue;

            const variantId = parseInt(gift.dataset.variantId);
            // Le mot-clé saisi "arme" le cadeau (comme le lien ?add-gift=), indépendamment de
            // la validation Shopify du code. Le montant minimum reste requis (with_minimum_amount).
            this.armedVariants.set(variantId, Date.now() + GIFT_TTL_MS);
            this.saveTtlMap(ARMED_COOKIE, this.armedVariants);
            // Ré-application volontaire : on lève le refus et on oublie l'ajout précédent.
            this.clearDismissedCookie(variantId);
            this.unmarkAdded(variantId);
            matched = true;
        }

        if (!matched) return;

        this.getCart().then((cart) => {
            if (cart) this.scheduleValidate(cart);
        });
    }

    // Détecte les codes de remise qui viennent d'être appliqués au panier (transition :
    // absents au passage précédent, présents et applicables maintenant). Pour un tel code,
    // c'est une action manuelle du client -> on lève le refus des cadeaux correspondants
    // (et on oublie qu'on les avait ajoutés) afin qu'ils soient réévalués et ré-ajoutés.
    clearDismissalsForNewlyAppliedCodes(cart) {
        const currentApplied = new Set(
            (cart.discount_codes || [])
                .filter((c) => c.applicable && c.code)
                .map((c) => c.code.trim().toUpperCase())
        );

        // Premier passage (chargement) : on mémorise l'état sans rien réinitialiser, sinon
        // un code déjà appliqué à l'arrivée effacerait un refus légitime.
        if(this.appliedCodes === null) {
            this.appliedCodes = currentApplied;
            return;
        }

        for(const code of currentApplied) {
            if(this.appliedCodes.has(code)) continue; // déjà appliqué avant : pas nouveau
            this.resetGiftDismissalForCode(code);
        }

        this.appliedCodes = currentApplied;
    }

    // Mode "un seul cadeau" : parmi les cadeaux éligibles, choisit celui à conserver = le plus
    // récemment déclenché (timestamp d'armement le plus élevé) ; à défaut celui déjà au panier ;
    // à défaut le premier éligible (ordre des blocs). Retourne null si aucun n'est éligible.
    pickKeeperVariant(cart) {
        let keeper = null;
        let bestScore = -1;

        for (const gift of this.gifts) {
            if(isNullish(gift.dataset.variantId) || isNullish(gift.dataset.tierType)) continue;

            const variantId = parseInt(gift.dataset.variantId);
            const isArmed = this.isInTtlMap(this.armedVariants, ARMED_COOKIE, variantId);
            if(!this.isGiftUnlocked(cart, gift, gift.dataset.tierType, isArmed)) continue;

            // Récence de l'armement (expiration = armement + 24h). Sinon, s'il est déjà au
            // panier on lui donne une petite priorité ; sinon score neutre (ordre des blocs).
            let score = this.armedVariants.get(variantId) || 0;
            if(score === 0 && this.isAlreadyOnCart(cart, variantId)) score = 1;

            if(score > bestScore) {
                bestScore = score;
                keeper = variantId;
            }
        }

        return keeper;
    }

    validateConditions(cart) {
        // Si le client vient de (ré)appliquer un code manuellement, on lève le refus
        // des cadeaux correspondants AVANT d'évaluer les conditions -> ré-ajout possible.
        this.clearDismissalsForNewlyAppliedCodes(cart);

        // Mode "un seul cadeau" : un seul gagnant conservé au panier, les autres retirés.
        const keeperVariant = this.singleGift ? this.pickKeeperVariant(cart) : null;

        for (const gift of this.gifts) {

            if(isNullish(gift.dataset.variantId) || isNullish(gift.dataset.tierType)) continue;

            const variantId = parseInt(gift.dataset.variantId);
            const tierType = gift.dataset.tierType;
            const isAlreadyOnCart = this.isAlreadyOnCart(cart, variantId);
            const isArmed = this.isInTtlMap(this.armedVariants, ARMED_COOKIE, variantId);
            const conditionMet = this.isGiftUnlocked(cart, gift, tierType, isArmed);

            // Mode "un seul cadeau" : un cadeau éligible qui n'est pas le gagnant est retiré
            // (remplacement) et non ajouté. Ce n'est pas un refus client -> pas de cookie dismissed.
            if(this.singleGift && conditionMet && variantId !== keeperVariant) {
                this.unmarkAdded(variantId);
                if(isAlreadyOnCart) {
                    this.removeGiftFromCart(variantId);
                }
                continue;
            }

            // Plus éligible (ex : panier repassé sous le seuil) : on retire le cadeau s'il
            // est encore au panier et on oublie qu'on l'avait ajouté nous-mêmes.
            // On NE supprime PAS dismissedVariants ici : si le client avait retiré le
            // cadeau manuellement, repasser au-dessus du seuil ne doit pas le réajouter
            // automatiquement (seule la resaisie du code ou ?add-gift= le relance).
            if(!conditionMet) {
                this.unmarkAdded(variantId);
                if(isAlreadyOnCart) {
                    this.removeGiftFromCart(variantId);
                }
                continue;
            }

            // Éligible et déjà au panier : on (re)marque qu'on le possède au panier.
            if(isAlreadyOnCart) {
                this.markAdded(variantId);
                continue;
            }

            // Éligible mais absent du panier, et le client l'avait refusé (retrait manuel
            // sans réapplication de code) : on respecte son choix 24h.
            if(this.isInTtlMap(this.dismissedVariants, DISMISSED_COOKIE, variantId)) {
                continue;
            }

            // Retrait manuel détecté : cookie refus 24h (pas de ré-ajout auto).
            if(this.isInTtlMap(this.addedVariants, ADDED_COOKIE, variantId)) {
                this.setDismissedCookie(variantId);
                this.unmarkAdded(variantId);
                continue;
            }

            // Première fois éligible : on ajoute (verrou anti-double-ajout).
            // markAdded UNIQUEMENT après succès API (sinon faux "retrait manuel" + boucles).
            if(!this.addInProgress.has(variantId)) {
                this.addInProgress.add(variantId);
                this.addGiftToCart(variantId)
                    .then((ok) => { if (ok) this.markAdded(variantId); })
                    .finally(() => this.addInProgress.delete(variantId));
            }
        }
    }

    removeGiftFromCart(variantId) {
        if(!this.deletePermission) return;

        const buttonRemove = document.querySelector(`cart-remove-button [data-variant-id="${variantId}"]`);
        const cartRemoveButton = buttonRemove ? buttonRemove.closest('cart-remove-button') : null;

        // Impossible de retirer sur cette page (drawer non rendu) : on ne pose pas le verrou,
        // sinon il resterait bloqué et empêcherait tout retrait futur.
        if(!cartRemoveButton || !this.cartItems) return;

        this.deletePermission = false;
        this.cartItems.updateQuantity(parseInt(cartRemoveButton.dataset.index), 0);

        setTimeout(() => {
            this.deletePermission = true;
        }, 2000);
    }

    // Le code est vérifié nativement par Shopify (validDiscountCode = remise applicable).
    //  - with_discount_code   : code valide (Shopify) -> on ajoute.
    //  - with_minimum_amount  : code valide (Shopify) ET montant minimum atteint -> on ajoute.
    // Le lien ?add-gift= (isArmed) arme l'intention marketing mais n'ajoute le cadeau
    // qu'après validation Shopify du code OU si le montant minimum est atteint avec le lien.
    // Pour with_minimum_amount, le lien ?add-gift= compte comme "code armé" : le montant
    // minimum reste obligatoire ; la réapplication manuelle du code relance tout.
    isGiftUnlocked(cart, gift, tierType, isArmed = false) {
        // Montant du panier AVANT réduction (original_total_price), pour que le code promo
        // n'empêche pas d'atteindre le minimum. Repli sur total_price si absent.
        const cartTotal = cart.original_total_price ?? cart.total_price;

        // Aucun cadeau tant que le panier est vide
        if(cartTotal <= 0) return false;

        // Garde-fou commun : variante disponible (data-variant-available côté Liquid).
        if(gift.dataset.variantAvailable !== 'true') return false;

        const hasValidCode = this.validDiscountCode(cart, gift.dataset.discountCode);

        if(tierType === 'with_discount_code') {
            // COUPLÉ : le cadeau n'est offert que si Shopify applique réellement la remise.
            // (le mot-clé / lien n'ouvre PAS ce bloc — c'est le déclencheur "vrai code".)
            return hasValidCode;
        }

        if(tierType === 'with_minimum_amount') {
            const minimumAmount = parseFloat(gift.dataset.minimumAmount) || 0;
            const minimumInCents = Math.round(minimumAmount * 100);
            const minimumReached = cartTotal >= minimumInCents;

            if (!minimumReached) return false;

            // DISTINCT : montant atteint + code déclenché (mot-clé saisi/lien armé) OU
            // vraie remise Shopify. Le code n'a PAS besoin d'être validé par Shopify ici.
            return isArmed || hasValidCode;
        }

        return false;
    }

    // Utilisé UNIQUEMENT par le bloc with_discount_code : le code doit correspondre à une
    // remise réellement appliquée par Shopify (applicable: true).
    validDiscountCode(cart, discountCode) {
        if(!cart.discount_codes || isNullish(discountCode)) return false;

        const normalizedTarget = discountCode.trim().toUpperCase();

        return cart.discount_codes.some((code) =>
            code.applicable && code.code && code.code.trim().toUpperCase() === normalizedTarget
        );
    }
 
    isAlreadyOnCart(cart, variantId) {
        if(!cart.items) return false;

        return cart.items.some((item) => item.variant_id == variantId);
    }
 
    updateCart(response) {
        if(this.cart) {
            this.cart.renderContents(response);
        }
    }
 
    publishCartUpdate(cartData) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        publish(PUB_SUB_EVENTS.cartUpdate, {
            source: this.sourceTag,
            cartData: cartData,
        });
    }

    async addGiftToCart(variantId) {
        if (Date.now() < this.rateLimitUntil) return false;

        let bodyConfig = JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
        });

        if(this.cart) {
            bodyConfig = JSON.stringify({
                items: [{ id: variantId, quantity: 1 }],
                sections: this.cart.getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname
            });
        }

        try {
            const response = await fetch(this.cartURLs.add, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: bodyConfig,
            });

            if (response.status === 429) {
                this.rateLimitUntil = Date.now() + 15000;
                console.warn('[gift-dispatcher] Rate limit Shopify (429) sur cart/add — pause 15s');
                return false;
            }

            const data = await response.json();

            if (!response.ok || data.status) {
                console.warn('[gift-dispatcher] Échec ajout cadeau:', data.description || data.message || response.status);
                return false;
            }

            this.updateCart(data);
            this.publishCartUpdate(data);
            return true;
        } catch (error) {
            console.error('[gift-dispatcher] addGiftToCart:', error);
            return false;
        }
    }
}
 
customElements.define('gift-dispatcher', GiftDispatcher);